import React, {useEffect, useRef, useState} from "react";
import {Alert, Breadcrumbs, Button, Divider, Input, InputLabel, Link, MenuItem, Paper, Select, Stack, Typography} from "@mui/material";
import {LexicalEditor} from "lexical";
import {TextEditor} from "../../components/editor/TextEditor";
import useApiCall, {makeApiCall} from "../../hooks/CancellableApiCall";
import {Link as RouterLink, useLocation, useNavigate, useParams} from "react-router-dom";
import {$generateHtmlFromNodes} from '@lexical/html';
import {DetailedResourceResponse} from "../../data/DetailedResourceResponse";
import {Division} from "../../data/Division";

interface EditState {
    id: number;
    title: string;
    type: string;
    content: string;
    privateResource: boolean;
    identifier: string;
}

const identifierRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]$/

export const ResourceFormPage = () => {
    const params = useParams()
    const location = useLocation()
    const navigate = useNavigate()

    // If navigated with state, we're in edit mode
    const editState = (location.state as EditState | null) ?? null;
    const isEditMode = editState !== null;

    const editorRef = useRef<LexicalEditor>(null)
    const [type, setType] = useState(editState?.type ?? "content")
    const [privacy, setPrivacy] = useState(editState?.privateResource ? 1 : 0)
    const [title, setTitle] = useState(editState?.title ?? "")
    const [identifier, setIdentifier] = useState(editState?.identifier ?? "")
    const [url, setUrl] = useState(editState?.type === "link" ? (editState?.content ?? "") : "")
    const [error, setError] = useState<string | null>(null)
    const [identifierError, setIdentifierError] = useState<string | null>(null)
    const [checkingIdentifier, setCheckingIdentifier] = useState(false)

    const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/

    const divisionIdentifierLink = params.divisionIdentifier?.replaceAll("-", "_")
    const divisionIdentifierUrl = params.divisionIdentifier

    // Debounced identifier availability check
    const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const handleIdentifierChange = (value: string) => {
        setIdentifier(value)
        setError(null)
        setIdentifierError(null)

        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current)
        }

        if (!value) {
            setCheckingIdentifier(false)
            return
        }

        if (!identifierRegex.test(value)) {
            setIdentifierError("Identifier must be alphanumeric with hyphens, at least 3 characters.")
            setCheckingIdentifier(false)
            return
        }

        // In edit mode, if identifier hasn't changed, it's fine
        if (isEditMode && value === editState.identifier) {
            setCheckingIdentifier(false)
            return
        }

        setCheckingIdentifier(true)
        checkTimeoutRef.current = setTimeout(() => {
            fetch(`/api/resource/${divisionIdentifierLink}/identifier-available/${encodeURIComponent(value)}`)
                .then(res => {
                    if (!res.ok) throw new Error("Request failed")
                    return res.json()
                })
                .then((available: boolean) => {
                    setCheckingIdentifier(false)
                    if (!available) {
                        setIdentifierError("This identifier is already in use. Please choose a different one.")
                    } else {
                        setIdentifierError(null)
                    }
                })
                .catch(() => {
                    setCheckingIdentifier(false)
                })
        }, 400)
    }

    useEffect(() => {
        return () => {
            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current)
            }
        }
    }, [])

    let entryType
    if (type === "content") {
        entryType = <TextEditor ref={editorRef} initialContent={isEditMode ? editState.content : undefined}/>
    } else {
        entryType = <>
            <InputLabel>Resource URL</InputLabel>
            <Input placeholder={"https://example.com"} size="small"
                   sx={{width: 500}}
                   value={url}
                   onChange={(event) => {
                       setUrl(event.target.value)
                       setError(null)
                   }}/>
            <Divider/>
        </>
    }

    const handleSubmit = () => {
        if (type === "link" && !urlRegex.test(url)) {
            setError("URL must start with http:// or https:// and be a valid link.")
            return
        }
        if (identifier && !identifierRegex.test(identifier)) {
            setError("Identifier must be alphanumeric with hyphens, at least 3 characters.")
            return
        }
        if (identifierError) {
            return
        }
        setError(null)

        if (isEditMode) {
            // Edit mode — PATCH content
            if (type === "content" && editorRef.current) {
                editorRef.current.getEditorState().read(() => {
                    makeApiCall({
                        url: `/api/resource/${divisionIdentifierLink}/${editState.id}/content`,
                        method: "PATCH",
                        body: {
                            newTitle: title,
                            newContent: $generateHtmlFromNodes(editorRef.current!),
                            newIdentifier: identifier,
                            newPrivacy: privacy === 1
                        },
                        onLoadedCallback: () => {
                            navigate(`/resource/${divisionIdentifierUrl}/${identifier.toLowerCase()}`)
                        },
                        onError: () => {
                            setError("Failed to update resource. Please try again.")
                        }
                    })
                })
            } else {
                makeApiCall({
                    url: `/api/resource/${divisionIdentifierLink}/${editState.id}/content`,
                    method: "PATCH",
                    body: {
                        newTitle: title,
                        newContent: url,
                        newIdentifier: identifier,
                        newPrivacy: privacy === 1
                    },
                    onLoadedCallback: () => {
                        navigate(`/resources/${divisionIdentifierUrl}`)
                    },
                    onError: () => {
                        setError("Failed to update resource. Please try again.")
                    }
                })
            }
        } else {
            // Create mode — POST new resource
            if (type === "content" && editorRef.current) {
                editorRef.current.getEditorState().read(() => {
                    makeApiCall<DetailedResourceResponse>({
                        url: `/api/resource/${divisionIdentifierLink}/create`,
                        method: "POST",
                        body: {
                            title: title,
                            type: type,
                            content: $generateHtmlFromNodes(editorRef.current!),
                            privateResource: privacy === 1,
                            identifier: identifier || undefined
                        },
                        onLoadedCallback: (data) => {
                            navigate(`/resource/${divisionIdentifierUrl}/${data.identifier}`)
                        },
                        onError: () => {
                            setError("Failed to create resource. Please try again.")
                        }
                    })
                })
            } else {
                makeApiCall({
                    url: `/api/resource/${divisionIdentifierLink}/create`,
                    method: "POST",
                    body: {
                        title: title,
                        type: type,
                        content: url,
                        privateResource: privacy === 1,
                        identifier: identifier || undefined
                    },
                    onLoadedCallback: () => {
                        navigate(`/resources/${divisionIdentifierUrl}`)
                    },
                    onError: () => {
                        setError("Failed to create resource. Please try again.")
                    }
                })
            }
        }
    }

    const divisions = useApiCall<Division[]>({
        url: `/api/division/list-divisions`,
    });
    const divisionDisplayName = divisions.data?.find(d => d.identifier === divisionIdentifierLink)?.displayName
        ?? divisionIdentifierLink?.replaceAll("_", " ") ?? "";

    return (
        <Stack spacing={1}>
            <Breadcrumbs sx={{mb: 1, fontSize: "0.85rem"}}>
                <Link component={RouterLink} to="/" underline="hover" color="text.secondary">
                    Home
                </Link>
                <Link component={RouterLink} to="/resources" underline="hover" color="text.secondary">
                    Resources
                </Link>
                <Link component={RouterLink} to={`/resources/${divisionIdentifierUrl}`} underline="hover" color="text.secondary">
                    {divisionDisplayName}
                </Link>
                <Typography color="text.primary" fontSize="0.85rem">
                    {isEditMode ? "Edit Resource" : "New Resource"}
                </Typography>
            </Breadcrumbs>

            <Typography variant="h6" gutterBottom>
                {isEditMode ? "Edit Resource" : "Resource Creation"}
            </Typography>

            <Paper variant="outlined" style={{
                marginTop: "16px",
                padding: "16px"
            }}>
                <InputLabel>Title</InputLabel>
                <Input placeholder={"Title..."} size="small"
                       sx={{width: 500, pt: 1}}
                       value={title}
                       onChange={(event) => setTitle(event.target.value)}
                />
                <InputLabel sx={{pt: 2}}>Identifier</InputLabel>
                <Input
                    placeholder={isEditMode ? "Identifier..." : (title.trim()
                        ? title.trim().replace(/&/g, " ").replace(/\//g, " ").toLowerCase().replace(/ +/g, "-").replace(/-+/g, "-")
                        : "Leave blank to auto-generate from title")}
                    size="small"
                    sx={{width: 500, pt: 1}}
                    value={identifier}
                    onChange={(event) => handleIdentifierChange(event.target.value)}
                />
                {identifierError && (
                    <Alert severity="error" sx={{mt: 1, maxWidth: 500}}>{identifierError}</Alert>
                )}
                {checkingIdentifier && (
                    <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: "block"}}>
                        Checking availability...
                    </Typography>
                )}
                <InputLabel sx={{pt: 2}}>Private</InputLabel>
                <Select
                    id={`select-privacy`}
                    value={Number(privacy)}
                    sx={{width: 500}}
                    onChange={(event) => {
                        setPrivacy(event.target.value as number)
                    }}
                    variant="standard"
                    label="Private Resource"
                >
                    <MenuItem key={1} value={1}>Yes</MenuItem>
                    <MenuItem key={0} value={0}>No</MenuItem>
                </Select>
                <InputLabel sx={{pt: 2}}>Type</InputLabel>
                <Select
                    id={`select-type`}
                    value={type}
                    sx={{width: 500, mb: 2}}
                    onChange={(event) => {
                        setType(event.target.value as string)
                    }}
                    variant="standard"
                    label="Resource Type"
                    disabled={isEditMode}
                >
                    <MenuItem key={"content"} value={"content"}>Content</MenuItem>
                    <MenuItem key={"link"} value={"link"}>Link</MenuItem>
                </Select>
                {entryType}
                {error && <Alert severity="error" sx={{mt: 1, mb: 1, maxWidth: 500}}>{error}</Alert>}
                <Button disabled={title.trim() === "" || !!identifierError || checkingIdentifier} color="success" onClick={handleSubmit}>
                    {isEditMode ? "Save Changes" : "Create"}
                </Button>
                <Button component={RouterLink} to={`/resources/${divisionIdentifierUrl}`}
                        color="warning">Return</Button>
            </Paper>
        </Stack>
    )
}
